import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Phone, Mail, MapPin, Building2, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api, { API_BASE } from "@/lib/api";
import { money, formatDate } from "@/lib/format";
import { getApiErrorMessage } from "@/lib/apiError";

function Section({ title, children }) {
    return (
        <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <div className="flex items-center gap-3 mb-3">
                <h2 className="font-display text-2xl text-[#0F2A47]">{title}</h2>
                <div className="flex-1 h-px bg-gradient-to-r from-[#B8862E]/40 to-transparent" />
            </div>
            {children}
        </motion.section>
    );
}

function displayLinePrice(value) {
    const numeric = Number(value || 0);
    return numeric > 0 ? money(numeric) : "TBD";
}

function displayLineAmount(row) {
    const qty = Number(row.qty || 0);
    const unit = Number(row.unit_price || 0);
    return unit > 0 ? money(qty * unit) : "TBD";
}

function LineTable({ rows }) {
    if (!rows || rows.length === 0) return null;
    return (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="grid grid-cols-12 bg-slate-50 px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500">
                <div className="col-span-7">Description</div>
                <div className="col-span-1 text-right">Qty</div>
                <div className="col-span-2 text-right">Unit</div>
                <div className="col-span-2 text-right">Amount</div>
            </div>
            {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-12 px-4 py-3 border-t border-slate-100">
                    <div className="col-span-7 text-sm">{r.description}</div>
                    <div className="col-span-1 text-right tabular text-sm">{r.qty}</div>
                    <div className="col-span-2 text-right tabular text-sm">{displayLinePrice(r.unit_price)}</div>
                    <div className="col-span-2 text-right tabular text-sm font-medium">{displayLineAmount(r)}</div>
                </div>
            ))}
        </div>
    );
}

export default function PublicQuote() {
    const { shareToken } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/public/quotes/${shareToken}`)
            .then((r) => setData(r.data))
            .catch((err) => setError(getApiErrorMessage(err, "Quote not found")));
    }, [shareToken]);

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] p-6">
                <Card className="max-w-md p-8 text-center rounded-2xl border-slate-200">
                    <div className="text-2xl font-display text-slate-900">Quote not available</div>
                    <div className="text-sm text-slate-500 mt-2">{error}</div>
                </Card>
            </div>
        );
    }
    if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

    const { customer = {}, home = {}, totals = {}, company = {}, associate = {} } = data;
    const downloadPdf = () => {
        // Use direct navigation rather than window.open() to avoid popup blockers
        const url = `${API_BASE}/public/quotes/${shareToken}/pdf`;
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener";
        a.download = `${data.quote_number || "quote"}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    return (
        <div className="min-h-screen bg-[hsl(var(--background))]">
            <div className="max-w-4xl mx-auto p-4 sm:p-8 space-y-8">
                {/* Hero */}
                <Card className="relative overflow-hidden rounded-2xl border-slate-200 bg-white">
                    <div className="relative h-[180px] sm:h-[220px] overflow-hidden">
                        <img
                            src="https://images.pexels.com/photos/5177211/pexels-photo-5177211.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=1200"
                            alt="Modern home"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A47] via-[#0F2A47]/60 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 text-white">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/70">
                                <Building2 className="h-3 w-3" /> {company.name}
                            </div>
                            <h1 className="font-display text-3xl sm:text-4xl mt-1">Your Easy HomeSource Quote</h1>
                            <div className="text-sm text-white/80 mt-1" data-testid="public-quote-number">
                                Quote #{data.quote_number} · {formatDate(data.quote_date)}
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Greeting + summary card */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
                    <Card className="rounded-2xl border-slate-200 bg-white p-6 sm:p-7">
                        <div className="text-xs uppercase tracking-wider text-slate-500">Prepared for</div>
                        <div className="font-display text-2xl text-slate-900 mt-1">{customer.first_name} {customer.last_name}</div>
                        <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                            Thank you for considering Easy HomeSource for your new home. Below is a personalized
                            estimate for your selected home and quoted services. Review the line items carefully;
                            delivery, site work, utility connections, installation, permits, and other project items
                            are included only when specifically listed with pricing in this quote. Items marked TBD
                            require confirmation before final approval.
                        </p>
                        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                                <div className="text-[11px] uppercase text-slate-500">Beds</div>
                                <div className="font-medium text-slate-900">{home.beds || "—"}</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                                <div className="text-[11px] uppercase text-slate-500">Baths</div>
                                <div className="font-medium text-slate-900">{home.baths || "—"}</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                                <div className="text-[11px] uppercase text-slate-500">Sq Ft</div>
                                <div className="font-medium text-slate-900">{home.sqft || "—"}</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                                <div className="text-[11px] uppercase text-slate-500">Type</div>
                                <div className="font-medium text-slate-900">{home.home_type || "—"}</div>
                            </div>
                        </div>
                    </Card>

                    <Card className="rounded-2xl bg-white border-[rgba(184,134,46,0.25)] p-6 shadow-[0_1px_0_rgba(15,42,71,0.06),0_18px_60px_rgba(15,42,71,0.10)] lg:sticky lg:top-6 h-fit">
                        <div className="text-xs uppercase tracking-wider text-slate-500">Estimated total</div>
                        <div className="font-display text-4xl text-[#0F2A47] tabular mt-1" data-testid="public-quote-total-amount">
                            {money(totals.grand_total || 0)}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            Based on selected priced line items and applicable sales tax. TBD items are not reflected in the total.
                        </div>
                        <div className="my-4 h-px bg-[#B8862E]/30" />
                        <div className="space-y-1.5 text-sm">
                            <Row label="Home subtotal" value={money(totals.home_subtotal || 0)} />
                            <Row label="Delivery" value={money(totals.site_work_subtotal || 0)} />
                            <Row label="Site Work" value={money(totals.mandatory_services_subtotal || 0)} />
                            <Row label="Add-ons" value={money(totals.addons_subtotal || 0)} />
                            {(totals.discounts_total || 0) > 0 && (
                                <Row label="Discounts" value={`- ${money(totals.discounts_total)}`} />
                            )}
                            <Row label="Subtotal" value={money(totals.subtotal || 0)} />
                            <Row label={`Sales tax (${((totals.sales_tax_rate || 0) * 100).toFixed(2)}%)`} value={money(totals.sales_tax || 0)} />
                        </div>
                        <Button onClick={downloadPdf} className="mt-5 w-full bg-[#0F2A47] hover:bg-[#14385E] text-white focus-visible:ring-2 focus-visible:ring-[#B8862E]" data-testid="public-quote-download-pdf-button">
                            <Download className="mr-2 h-4 w-4" /> Download PDF
                        </Button>
                    </Card>
                </div>

                {/* Home details */}
                <Section title="Your home">
                    <Card className="rounded-2xl border-slate-200 bg-white p-6">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6 text-sm">
                            <div><div className="text-xs text-slate-500">Manufacturer</div>{home.manufacturer || "—"}</div>
                            <div><div className="text-xs text-slate-500">Series</div>{home.series || "—"}</div>
                            <div><div className="text-xs text-slate-500">Model</div>{home.model_name || "—"}</div>
                            <div><div className="text-xs text-slate-500">Beds / Baths</div>{home.beds || "—"} / {home.baths || "—"}</div>
                            <div><div className="text-xs text-slate-500">Square Feet</div>{home.sqft || "—"}</div>
                            <div><div className="text-xs text-slate-500">Dimensions</div>{home.dimensions || "—"}</div>
                        </div>
                        <div className="mt-4 flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                            <div>
                                <div className="text-xs uppercase tracking-wider text-slate-500">Base home price</div>
                            </div>
                            <div className="font-display text-xl text-[#0F2A47] tabular">{money(data.base_price)}</div>
                        </div>
                    </Card>
                </Section>

                {/* Options */}
                {data.options?.length > 0 && (
                    <Section title="Options & upgrades"><LineTable rows={data.options} /></Section>
                )}
                {data.mandatory_services?.length > 0 && (
                    <Section title="Site Work"><LineTable rows={data.mandatory_services} /></Section>
                )}
                {data.addons?.length > 0 && (
                    <Section title="Selected add-ons"><LineTable rows={data.addons} /></Section>
                )}
                {data.site_work?.length > 0 && (
                    <Section title="Delivery"><LineTable rows={data.site_work} /></Section>
                )}
                {data.discounts?.length > 0 && (
                    <Section title="Discounts">
                        <Card className="rounded-xl border-slate-200 bg-white p-4 space-y-2">
                            {data.discounts.map((d, i) => (
                                <div key={i} className="flex justify-between text-sm">
                                    <span>{d.description}</span>
                                    <span className="tabular">- {money(d.amount)}</span>
                                </div>
                            ))}
                        </Card>
                    </Section>
                )}

                {(data.financing_notes || data.customer_notes) && (
                    <Section title="Financing & notes">
                        <Card className="rounded-2xl border-slate-200 bg-white p-6 space-y-4 text-sm">
                            {data.financing_notes && (
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-slate-500">Financing & payment</div>
                                    <div className="mt-1 whitespace-pre-wrap">{data.financing_notes}</div>
                                </div>
                            )}
                            {data.customer_notes && (
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-slate-500">A note from your associate</div>
                                    <div className="mt-1 whitespace-pre-wrap">{data.customer_notes}</div>
                                </div>
                            )}
                        </Card>
                    </Section>
                )}

                {data.next_steps && (
                    <Section title="Next steps">
                        <Card className="rounded-2xl border-[rgba(184,134,46,0.25)] bg-white p-6 text-sm whitespace-pre-wrap">
                            {data.next_steps}
                        </Card>
                    </Section>
                )}

                {/* Trust + contact */}
                <Section title="Your housing consultant">
                    <Card className="rounded-2xl border-slate-200 bg-white p-6">
                        <div className="flex flex-wrap items-start justify-between gap-6">
                            <div>
                                <div className="font-display text-xl text-slate-900">{associate.name}</div>
                                <div className="mt-2 space-y-1.5 text-sm text-slate-700">
                                    {associate.phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {associate.phone}</div>}
                                    {associate.email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {associate.email}</div>}
                                </div>
                            </div>
                            <div className="text-sm text-slate-700">
                                <div className="font-medium text-slate-900">{company.name}</div>
                                <div className="flex items-center gap-2 mt-1"><MapPin className="h-3.5 w-3.5" /> {company.address}</div>
                                <div className="flex items-center gap-2 mt-1"><Phone className="h-3.5 w-3.5" /> {company.phone}</div>
                                <div className="flex items-center gap-2 mt-1"><Mail className="h-3.5 w-3.5" /> {company.email}</div>
                            </div>
                        </div>
                        <div className="mt-5 flex flex-wrap gap-2">
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700 inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Licensed & insured</span>
                        </div>
                    </Card>
                </Section>

                {data.disclaimer && (
                    <div className="text-xs text-slate-500 leading-relaxed border-t border-slate-200 pt-6">
                        {data.disclaimer}
                    </div>
                )}
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between text-slate-700">
            <span>{label}</span>
            <span className="tabular">{value}</span>
        </div>
    );
}
